import Fuse from "fuse.js";

const LEAGUE_FILES = {
  NFL: "/data/players-nfl.json",
};

const FUSE_OPTIONS = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "team", weight: 0.15 },
    { name: "position", weight: 0.15 },
  ],
  threshold: 0.35,
  includeScore: true,
  minMatchCharLength: 2,
};

let players = [];
let playerIndex = null;
let playerMap = {};

/**
 * Load player data for a given league and initialize the search index.
 *
 * @param {string} league — League identifier (e.g., "NFL").
 * @returns {Promise<Array>} — The loaded player array.
 */
export const loadPlayers = async (league = "NFL") => {
  const filePath = LEAGUE_FILES[league];

  if (!filePath) {
    throw new Error(`Unknown league: ${league}`);
  }

  const response = await fetch(filePath);

  if (!response.ok) {
    throw new Error(`Failed to load player data: ${response.status}`);
  }

  players = await response.json();
  playerIndex = new Fuse(players, FUSE_OPTIONS);
  playerMap = {};

  for (const player of players) {
    playerMap[player.id] = player;
  }

  return players;
};

/**
 * Search the loaded player list with fuzzy matching.
 *
 * @param {string} query — Search string (name, team, position, or combination).
 * @param {number} limit — Maximum number of results to return.
 * @returns {Array<{ item: object, score: number }>} — Ranked matches with scores (lower is better).
 */
export const searchPlayers = (query, limit = 5) => {
  if (!playerIndex) {
    return [];
  }

  if (!query || typeof query !== "string") {
    return [];
  }

  const trimmed = query.trim();

  // Ignore very short queries — single characters produce noisy results,
  // especially from speech-to-text fragments like "uh" or "a".
  if (trimmed.length < 2) {
    return [];
  }

  return playerIndex.search(trimmed, { limit });
};

/**
 * Get a single player by their unique ID.
 *
 * @param {string} id — Player ID (e.g., "nfl-patrick-mahomes").
 * @returns {object|null} — The player object, or null if not found.
 */
export const getPlayerById = id => {
  return playerMap[id] || null;
};

/**
 * Get all currently loaded players.
 *
 * @returns {Array} — The full player array.
 */
export const getAllPlayers = () => players;
